import {defineNuxtPlugin, useHead} from '#app';
import { type ExportedGlobalComposer} from 'vue-i18n';

interface NuxtI18nCustom {
    locale: string;
    setLocale: (locale: string) => Promise<void> | void;
    getLocaleCookie: () => string | undefined;
    getBrowserLocale?: () => string | undefined;
}

interface NuxtAppCustom {
    $i18n: ExportedGlobalComposer & NuxtI18nCustom;
    $changeLocale: (locale: string) => void;
}

const SUPPORTED_LOCALES = ['en', 'es', 'vi', 'ko', 'ja'] as const;

export default defineNuxtPlugin((nuxtApp) => {
    const $i18n = nuxtApp.$i18n as NuxtAppCustom['$i18n'];

    const cookieLocale = $i18n.getLocaleCookie?.();
    const browserLocale = process.client ? $i18n.getBrowserLocale?.() : undefined;
    const candidate = cookieLocale || browserLocale || 'en';
    const initialLocale: string = (SUPPORTED_LOCALES as readonly string[]).includes(candidate)
        ? candidate
        : 'en';

    const reactiveLocale = ref($i18n.locale);
    // setLocale is called but not awaited -- @nuxtjs/i18n's own
    // route-locale-detect plugin (which runs before us via `dependsOn: i18n:plugin`)
    // has already set the locale to the detected value during its setup, so this
    // call is typically a no-op. We still fire it to cover any edge case where
    // the route detection diverges from cookie/browser. The reveal-after-hydration
    // gating below (paired with the inline anti-FOUC hide) prevents the en flash.
    $i18n.setLocale(initialLocale);
    reactiveLocale.value = initialLocale;

    useHead({
        htmlAttrs: {
            lang: initialLocale
        }
    });

    if (process.client) {
        // The inline anti-FOUC script (package/config/i18nAntiFouc.ts, put in <head>
        // by nuxt.config.ts) hides <html>
        // (visibility:hidden) before first paint whenever the resolved locale is
        // non-en, so the prerendered English text never flashes before vue-i18n
        // re-renders into the target locale. We reveal again once hydration has
        // fully resolved: `app:suspense:resolve` fires after every async component
        // and async setup under the root <Suspense> has hydrated, and a `nextTick` +
        // double rAF guarantees the locale-patched DOM is laid out and about to paint.
        //
        // Not `app:mounted`: it fires when the root app mounts, before that async
        // subtree resolves. The renewal pages swap their v-html content to the client
        // locale only once that subtree mounts (`useRenewalHydrationKey`), so revealing
        // on `app:mounted` showed the English prerender for ~0.2s on desktop and ~1s
        // with 4x CPU throttling (measured on dev, TSK-12194).
        //
        // IMPORTANT: we deliberately do NOT gate the reveal on inspecting body text.
        // The previous implementation polled `body.innerText` for the absence of
        // English UI strings, but innerText is empty/unreliable while <html> is
        // visibility:hidden and the heuristic frequently never matched -- so the
        // reveal fell through to a multi-second timeout, leaving users on a blank
        // white page on every load/refresh (and the onboarding SDK overlay, which
        // renders outside the hidden tree, showing alone). The hydration signal
        // always fires, so non-en visitors stay hidden only as long as hydration
        // actually takes; the inline script's safety timeout is the backstop for a
        // JS boot that never gets here.
        const revealDocument = () => {
            const safety = (window as any).__i18nSafety;
            if (safety) {
                clearTimeout(safety);
                (window as any).__i18nSafety = null;
            }
            const el = document.documentElement;
            el.style.visibility = '';
            el.removeAttribute('data-i18n-pending');
        };

        // En visitors are never hidden by the inline script, so this is a harmless
        // no-op for them; non-en visitors get un-hidden on the first frame painted
        // after hydration resolves.
        nuxtApp.hook('app:suspense:resolve', async () => {
            await nextTick();
            requestAnimationFrame(() => requestAnimationFrame(revealDocument));
        });

        watch(reactiveLocale, (newLocale) => {
            useHead({
                htmlAttrs: {
                    lang: newLocale
                }
            });
        });
    }

    return {
        provide: {
            trans: (id: string) => ($i18n as any).t(id)
        }
    };
});
